import React, { useState, useEffect } from 'react';
import { 
  Calendar, Plus, Trash2, Pencil, GripVertical, Save,
  Loader2, Check, X, Palette
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { adminCalendarioAPI } from '../api';
import { toast } from '../hooks/use-toast';

const COLORES_PREDEFINIDOS = [
  { nombre: 'Rojo', valor: '#EF4444' },
  { nombre: 'Naranja', valor: '#F59E0B' },
  { nombre: 'Verde', valor: '#10B981' },
  { nombre: 'Azul', valor: '#3B82F6' },
  { nombre: 'Morado', valor: '#8B5CF6' },
  { nombre: 'Rosa', valor: '#EC4899' },
  { nombre: 'Gris', valor: '#6B7280' },
  { nombre: 'Índigo', valor: '#4F46E5' },
];

const CategoriasCalendario = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);

  const [form, setForm] = useState({
    nombre: '',
    valor: '',
    color: '#3B82F6',
    icono: 'calendar',
    orden: 0
  });

  useEffect(() => {
    loadCategorias();
  }, []);

  const loadCategorias = async () => {
    try {
      setLoading(true);
      const response = await adminCalendarioAPI.getCategorias();
      setCategorias(response.data);
    } catch (error) {
      console.error('Error cargando categorías:', error);
      toast({ title: "Error", description: "No se pudieron cargar las categorías", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      nombre: '',
      valor: '',
      color: '#3B82F6',
      icono: 'calendar',
      orden: categorias.length
    });
    setEditingCategoria(null);
  };

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setForm({
      nombre: categoria.nombre,
      valor: categoria.valor,
      color: categoria.color,
      icono: categoria.icono,
      orden: categoria.orden
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingCategoria) {
        await adminCalendarioAPI.updateCategoria(editingCategoria.id, {
          nombre: form.nombre,
          color: form.color,
          icono: form.icono,
          orden: form.orden
        });
        toast({ title: "Categoría actualizada" });
      } else {
        // Generar valor automático desde el nombre
        const valor = form.nombre.toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Quitar acentos
          .replace(/\s+/g, '_') // Espacios por guiones bajos
          .replace(/[^a-z0-9_]/g, ''); // Solo alfanuméricos
        
        await adminCalendarioAPI.createCategoria({
          ...form,
          valor
        });
        toast({ title: "Categoría creada" });
      }
      setIsDialogOpen(false);
      resetForm();
      loadCategorias();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo guardar la categoría",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (categoria) => {
    try {
      await adminCalendarioAPI.updateCategoria(categoria.id, { activo: !categoria.activo });
      toast({ title: categoria.activo ? "Categoría desactivada" : "Categoría activada" });
      loadCategorias();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDelete = async (categoriaId) => {
    if (!window.confirm('¿Eliminar esta categoría? Los eventos existentes mantendrán su categoría actual.')) return;
    
    try {
      await adminCalendarioAPI.deleteCategoria(categoriaId);
      toast({ title: "Categoría eliminada" });
      loadCategorias();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6" /> Categorías de Calendario
          </h1>
          <p className="text-gray-600">Gestiona las categorías para clasificar los eventos (juicios, audiencias, etc.)</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva categoría
        </Button>
      </div>

      {/* Lista de categorías */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Categorías disponibles</CardTitle>
        </CardHeader>
        <CardContent>
          {categorias.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No hay categorías. Se crearán las predeterminadas automáticamente.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {categorias.map((cat, index) => (
                <div 
                  key={cat.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${cat.activo ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-gray-400">
                      <GripVertical className="w-4 h-4" />
                      <span className="text-sm font-mono w-6 text-center">{cat.orden}</span>
                    </div>
                    
                    <div 
                      className="w-8 h-8 rounded-full border-2"
                      style={{ backgroundColor: cat.color, borderColor: cat.color }}
                    />
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{cat.nombre}</span>
                        {!cat.activo && <Badge variant="outline" className="text-xs">Inactiva</Badge>}
                      </div>
                      <span className="text-xs text-gray-500 font-mono">{cat.valor}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={cat.activo} 
                        onCheckedChange={() => handleToggleActivo(cat)}
                      />
                      <span className="text-sm text-gray-600">{cat.activo ? 'Activa' : 'Inactiva'}</span>
                    </div>
                    
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(cat)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(cat.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Vista previa</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {categorias.filter(c => c.activo).map(cat => (
              <Badge 
                key={cat.id}
                style={{ backgroundColor: cat.color, color: 'white' }}
                className="text-sm px-3 py-1"
              >
                {cat.nombre}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategoria ? 'Editar' : 'Nueva'} categoría</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input 
                value={form.nombre} 
                onChange={(e) => setForm({...form, nombre: e.target.value})} 
                placeholder="Ej: Juicio Oral"
                required 
                disabled={editingCategoria}
              />
              {editingCategoria && (
                <p className="text-xs text-gray-500 mt-1">El nombre no se puede cambiar para mantener la coherencia</p>
              )}
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-4 mt-2">
                <div 
                  className="w-10 h-10 rounded-lg border-2 cursor-pointer"
                  style={{ backgroundColor: form.color, borderColor: form.color }}
                />
                <div className="flex flex-wrap gap-2">
                  {COLORES_PREDEFINIDOS.map(color => (
                    <button
                      key={color.valor}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${form.color === color.valor ? 'scale-110 ring-2 ring-offset-2 ring-blue-500' : 'hover:scale-105'}`}
                      style={{ backgroundColor: color.valor, borderColor: color.valor }}
                      onClick={() => setForm({...form, color: color.valor})}
                      title={color.nombre}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-xs">Color personalizado</Label>
                <Input 
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({...form, color: e.target.value})}
                  className="w-20 h-8 p-0 cursor-pointer"
                />
              </div>
            </div>

            <div>
              <Label>Orden de visualización</Label>
              <Input 
                type="number"
                value={form.orden}
                onChange={(e) => setForm({...form, orden: parseInt(e.target.value) || 0})}
                min={0}
              />
              <p className="text-xs text-gray-500 mt-1">Menor número = aparece primero</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Save className="w-4 h-4 mr-2" /> Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriasCalendario;
